/**
 * Shared UI components for the managecare-agent admin UI.
 *
 * One file keeps the component count low while still giving each
 * component a clear name. All styling lives in styles.css.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";

import { lineDiff, diffCounts } from "./utils/diff.js";
import { useAuth } from "./AuthContext.jsx";

// ─── Layout shell ────────────────────────────────────────────────────

export function Layout({ children }) {
  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-column">
        <TopBar />
        <main className="main">{children}</main>
      </div>
    </div>
  );
}

// ─── Top bar ─────────────────────────────────────────────────────────
// Sticky strip above the main content. Content depends on mode:
//   - Platform mode: "Platform admin — pick an organization to start"
//   - Org mode:      colored org badge + name + switcher dropdown
//                    (+ Back to platform button for platform admins)
//   - No user:       hidden

export function TopBar() {
  const {
    user,
    orgs,
    currentOrg,
    inOrgContext,
    isPlatformAdmin,
    enterOrg,
    exitOrg,
  } = useAuth();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  if (!user) return null;

  // Platform mode strip — no org selected
  if (!inOrgContext) {
    return (
      <div className="topbar topbar-platform">
        <div className="topbar-left">
          <span className="topbar-mode-chip">Platform admin</span>
          <span className="topbar-hint">
            {isPlatformAdmin
              ? "Pick an organization from the list to enter its workspace."
              : "No active organization."}
          </span>
        </div>
        {isPlatformAdmin && (
          <button
            type="button"
            className="btn sm primary"
            onClick={() => nav("/admin/organizations")}
          >
            View organizations
          </button>
        )}
      </div>
    );
  }

  // Org mode strip — show org badge + switcher (for platform admin) or
  // static chip (for org users).
  return (
    <div className="topbar topbar-org">
      <div className="topbar-left" ref={menuRef}>
        <button
          type="button"
          className={`topbar-org-pill ${isPlatformAdmin ? "clickable" : ""}`}
          onClick={() => isPlatformAdmin && setMenuOpen((v) => !v)}
          disabled={!isPlatformAdmin}
        >
          <span
            className="topbar-org-avatar"
            style={{ background: currentOrg?.brand_color || "#0f0f0f" }}
          >
            {(currentOrg?.name || "?").charAt(0).toUpperCase()}
          </span>
          <div className="topbar-org-meta">
            <div className="topbar-org-label">Organization</div>
            <div className="topbar-org-name">{currentOrg?.name}</div>
          </div>
          {isPlatformAdmin && (
            <span className={`topbar-org-chevron ${menuOpen ? "open" : ""}`}>
              ›
            </span>
          )}
        </button>

        {menuOpen && isPlatformAdmin && (
          <div className="topbar-org-menu">
            <div className="topbar-org-menu-heading">Switch organization</div>
            {orgs.map((o) => (
              <button
                key={o._id}
                type="button"
                className={`topbar-org-menu-item ${
                  o._id === currentOrg?._id ? "active" : ""
                }`}
                onClick={() => {
                  enterOrg(o._id);
                  setMenuOpen(false);
                }}
              >
                <span
                  className="topbar-org-avatar small"
                  style={{ background: o.brand_color || "#0f0f0f" }}
                >
                  {(o.name || o._id).charAt(0).toUpperCase()}
                </span>
                <span style={{ flex: 1, textAlign: "left" }}>{o.name}</span>
                {o._id === currentOrg?._id && (
                  <span className="topbar-org-check">✓</span>
                )}
              </button>
            ))}
            <div className="topbar-org-menu-divider" />
            <button
              type="button"
              className="topbar-org-menu-item"
              onClick={() => {
                setMenuOpen(false);
                nav("/admin/organizations");
              }}
            >
              ⚙ Manage organizations
            </button>
          </div>
        )}
      </div>

      {isPlatformAdmin && (
        <button
          type="button"
          className="topbar-back-link"
          onClick={() => {
            exitOrg();
            nav("/admin/organizations");
          }}
          title="Return to platform admin view"
        >
          ← Back to platform
        </button>
      )}
    </div>
  );
}

// ─── Logo mark ───────────────────────────────────────────────────────
// Network-hub glyph inspired by the ManageCare brand. Drawn with
// currentColor so it inherits the surrounding text color — white on
// the dark sidebar, or dark on white backgrounds if reused elsewhere.
export function ManageCareMark({ size = 36 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="ManageCare"
    >
      {/* dashed orbit */}
      <circle
        cx="24"
        cy="24"
        r="19"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeDasharray="2 2.5"
        opacity="0.35"
      />

      {/* spokes */}
      <line x1="24" y1="24" x2="37" y2="14" stroke="currentColor" strokeWidth="1.5" opacity="0.85" />
      <line x1="24" y1="24" x2="8" y2="20" stroke="currentColor" strokeWidth="1.5" opacity="0.85" />
      <line
        x1="24" y1="24" x2="11" y2="37"
        stroke="currentColor" strokeWidth="1.2" strokeDasharray="2 2" opacity="0.55"
      />
      <line
        x1="24" y1="24" x2="36" y2="37"
        stroke="currentColor" strokeWidth="1.2" strokeDasharray="2 2" opacity="0.55"
      />

      {/* satellite nodes */}
      <circle cx="37" cy="14" r="3.2" fill="currentColor" opacity="0.9" />
      <circle cx="8"  cy="20" r="3.2" fill="currentColor" opacity="0.75" />
      <circle cx="11" cy="37" r="2.6" fill="currentColor" opacity="0.55" />
      <circle cx="36" cy="37" r="2.6" fill="currentColor" opacity="0.45" />

      {/* hub */}
      <circle cx="24" cy="24" r="6" fill="currentColor" />
      <circle cx="24" cy="24" r="2.2" fill="var(--color-sidebar-bg, #0f0f0f)" />
    </svg>
  );
}

// ─── Sidebar data model ──────────────────────────────────────────────
//
// Two distinct nav shapes based on whether the user has an active
// organization context:
//
//   - PLATFORM_SECTIONS: shown only to platform_admin when no org is
//     selected. Surfaces platform-level tools (Organizations). The
//     library/tenants are deliberately NOT in the nav because they
//     are org-scoped and there is no org picked.
//
//   - ORG_SECTIONS: shown whenever the user has an org in context
//     (always for org_users, and for platform admins who've picked
//     an org). Contains the library sections + tenants.
//
// Organization / Users / Platform Admin sections are added
// dynamically in the Sidebar render based on the current user role.

const PLATFORM_SECTIONS = [
  {
    id: "overview",
    label: "Overview",
    matcher: "/",
    items: [{ to: "/", label: "Dashboard", icon: "\u25A3", end: true }],
  },
];

const ORG_SECTIONS = [
  {
    id: "overview",
    label: "Overview",
    matcher: "/",
    items: [{ to: "/", label: "Dashboard", icon: "\u25A3", end: true }],
  },
  {
    id: "library",
    label: "Library",
    matcher: "/library",
    items: [
      { to: "/library/global-rules",      label: "Global Rules",     icon: "\u2630" },
      { to: "/library/tools",             label: "Tools",            icon: "\u25BD" },
      { to: "/library/protocols",         label: "Agent Rules",      icon: "\u25A4" },
      { to: "/library/agents",            label: "Agents",           icon: "\u25CE" },
      { to: "/library/built-in-agents",   label: "Built-in Agents",  icon: "\u25C8" },
      { to: "/library/specialties",       label: "Agent Groups",     icon: "\u25C9" },
      { to: "/library/post-call-actions", label: "Post-Call Actions", icon: "\u2611" },
    ],
  },
  {
    id: "tenants",
    label: "Tenants",
    matcher: "/tenants",
    items: [
      { to: "/tenants", label: "All Tenants", icon: "\u25A8", end: true },
      { to: "/tenants/updates", label: "Library Updates", icon: "\u21BB" },
    ],
  },
  {
    id: "testing",
    label: "Testing",
    matcher: "/testing-setup",
    items: [
      { to: "/testing-setup", label: "Testing Setup", icon: "\u2713", end: true },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    matcher: "/settings",
    items: [
      { to: "/settings/github", label: "GitHub Sync", icon: "\u2687", end: true },
    ],
  },
];

const SIDEBAR_STORAGE_KEY = "sidebar.open-sections";

function isPathInSection(pathname, section) {
  if (section.matcher === "/") return pathname === "/";
  return pathname.startsWith(section.matcher);
}

export function Sidebar() {
  const loc = useLocation();
  const nav = useNavigate();
  const auth = useAuth();
  const { user, isPlatformAdmin, inOrgContext, logout } = auth;
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    function onDocClick(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Pick the right section set. Platform admins without an org selected
  // see a slim platform-only sidebar. Everyone else sees the full
  // org-scoped nav.
  const sections = useMemo(
    () => (inOrgContext ? ORG_SECTIONS : PLATFORM_SECTIONS),
    [inOrgContext]
  );

  // Determine which section contains the current page.
  const activeSectionId = useMemo(() => {
    for (const s of sections) {
      if (isPathInSection(loc.pathname, s)) return s.id;
    }
    return null;
  }, [loc.pathname, sections]);

  // Load persisted open state from localStorage, plus whichever section
  // is currently active so users always see where they are.
  const [openSections, setOpenSections] = useState(() => {
    const base = new Set();
    try {
      const stored = JSON.parse(localStorage.getItem(SIDEBAR_STORAGE_KEY) || "[]");
      for (const id of stored) base.add(id);
    } catch {
      // ignore
    }
    return base;
  });

  // Whenever the route changes, ensure the active section is open.
  useEffect(() => {
    if (!activeSectionId) return;
    setOpenSections((prev) => {
      if (prev.has(activeSectionId)) return prev;
      const next = new Set(prev);
      next.add(activeSectionId);
      return next;
    });
  }, [activeSectionId]);

  // Persist open set so manual expand/collapse sticks across reloads.
  useEffect(() => {
    try {
      localStorage.setItem(
        SIDEBAR_STORAGE_KEY,
        JSON.stringify(Array.from(openSections))
      );
    } catch {
      // ignore
    }
  }, [openSections]);

  const toggleSection = (id) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <aside className="sidebar">
      <div className="brand">
        <div className="brand-icon">
          <ManageCareMark size={26} />
        </div>
        <div className="brand-wordmark">
          <div className="brand-title">Configural</div>
          <div className="brand-subtitle">Voice Agent Platform</div>
        </div>
      </div>

      <nav className="nav-body">
        {sections.map((section) => {
          const isOpen = openSections.has(section.id);
          const isActive = section.id === activeSectionId;
          return (
            <div
              key={section.id}
              className={`nav-section ${isOpen ? "open" : ""} ${isActive ? "active" : ""}`}
            >
              <button
                type="button"
                className="nav-section-header"
                onClick={() => toggleSection(section.id)}
                aria-expanded={isOpen}
              >
                {section.dot && (
                  <span className={`nav-heading-dot nav-heading-dot-${section.dot}`} />
                )}
                <span className="nav-section-label">{section.label}</span>
                <span className={`nav-section-chevron ${isOpen ? "open" : ""}`}>
                  {"\u203A"}
                </span>
              </button>

              <div className="nav-section-items">
                {isOpen &&
                  section.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        isActive ? "nav-link active" : "nav-link"
                      }
                    >
                      <span className="nav-icon">{item.icon}</span>
                      {item.label}
                    </NavLink>
                  ))}
              </div>
            </div>
          );
        })}

        {/* Organization section (Users) — only visible while the user
             is inside an org context AND has admin rights. */}
        {inOrgContext && (auth.isOrgAdmin || isPlatformAdmin) && (
          <div className="nav-section open">
            <div className="nav-section-header" style={{ cursor: "default" }}>
              <span className="nav-heading-dot nav-heading-dot-neutral" />
              <span className="nav-section-label">Organization</span>
            </div>
            <div className="nav-section-items">
              <NavLink
                to="/users"
                className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }
              >
                <span className="nav-icon">{"\u25A3"}</span>
                Users
              </NavLink>
              <NavLink
                to="/api-keys"
                className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }
              >
                <span className="nav-icon">{"\uD83D\uDD11"}</span>
                API Keys
              </NavLink>
            </div>
          </div>
        )}

        {/* Platform Admin section — only when a platform_admin is in
             platform mode (no org selected). When they enter an org
             the Back-to-platform link in the TopBar is used instead. */}
        {isPlatformAdmin && !inOrgContext && (
          <div className="nav-section open">
            <div className="nav-section-header" style={{ cursor: "default" }}>
              <span className="nav-heading-dot nav-heading-dot-platform" />
              <span className="nav-section-label">Platform Admin</span>
            </div>
            <div className="nav-section-items">
              <NavLink
                to="/admin/organizations"
                className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }
              >
                <span className="nav-icon">{"\u25C9"}</span>
                Organizations
              </NavLink>
            </div>
          </div>
        )}
      </nav>

      {/* User footer with logout */}
      {user && (
        <div className="user-menu" ref={userMenuRef}>
          <button
            type="button"
            className="user-menu-button"
            onClick={() => setUserMenuOpen((v) => !v)}
          >
            <span className="user-menu-avatar">
              {(user.name || user.email).charAt(0).toUpperCase()}
            </span>
            <div className="user-menu-meta">
              <div className="user-menu-name">{user.name || user.email}</div>
              <div className="user-menu-role">
                {user.role === "platform_admin"
                  ? "Platform admin"
                  : user.is_org_admin
                  ? "Org admin"
                  : "Org user"}
              </div>
            </div>
            <span className="user-menu-chevron">›</span>
          </button>
          {userMenuOpen && (
            <div className="user-menu-dropdown">
              <div className="user-menu-heading">
                {user.email}
              </div>
              <button
                type="button"
                className="user-menu-item"
                onClick={async () => {
                  setUserMenuOpen(false);
                  await logout();
                  nav("/login");
                }}
              >
                Log out
              </button>
            </div>
          )}
        </div>
      )}
    </aside>
  );
}

// ─── Page header ─────────────────────────────────────────────────────

export function PageHeader({ title, subtitle, breadcrumbs, actions }) {
  return (
    <div className="page-header">
      {breadcrumbs && breadcrumbs.length > 0 && (
        <div className="breadcrumbs">
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={i}>
              {crumb.to ? <Link to={crumb.to}>{crumb.label}</Link> : <span>{crumb.label}</span>}
              {i < breadcrumbs.length - 1 && <span> / </span>}
            </React.Fragment>
          ))}
        </div>
      )}
      <div className="title-row">
        <h1>{title}</h1>
        {actions && <div className="btn-row">{actions}</div>}
      </div>
      {subtitle && <div className="subtitle">{subtitle}</div>}
    </div>
  );
}

// ─── Tabs ────────────────────────────────────────────────────────────

export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="tabs">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          className={`tab ${active === tab.id ? "active" : ""}`}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
          {tab.badge != null && <span style={{ marginLeft: 6, opacity: 0.7 }}>{tab.badge}</span>}
        </button>
      ))}
    </div>
  );
}

// ─── Status badges ───────────────────────────────────────────────────

export function StatusBadge({ doc }) {
  if (!doc) return null;
  if (doc.is_live && doc.is_draft) return <span className="badge badge-live">Live</span>;
  if (doc.is_live && !doc.is_draft) return <span className="badge badge-live">Live · stale draft</span>;
  if (!doc.is_live && doc.is_draft) return <span className="badge badge-draft">Draft</span>;
  return <span className="badge badge-neutral">Inactive</span>;
}

export function VersionBadge({ version }) {
  if (version == null) return null;
  return <span className="badge badge-neutral mono">v{version}</span>;
}

export function ModeBadge({ mode }) {
  if (mode === "draft") return <span className="badge badge-draft">Draft Mode</span>;
  return <span className="badge badge-live">Live Mode</span>;
}

// ─── Version history list ───────────────────────────────────────────

export function VersionHistory({ versions, onSelect }) {
  if (!versions || versions.length === 0) {
    return <div className="muted small">No version history.</div>;
  }
  return (
    <ul className="version-list">
      {versions.map((v) => (
        <li key={`${v.version}-${v.created_at}`}>
          <div className="version-number">v{v.version}</div>
          <div className="version-meta">
            <div className="commit">{v.commit_msg || <span className="subtle">no message</span>}</div>
            <div className="author">
              by {v.created_by || "unknown"} · {formatDate(v.created_at)}
            </div>
          </div>
          <div className="btn-row">
            <StatusBadge doc={v} />
            {onSelect && (
              <button className="btn sm" onClick={() => onSelect(v)}>
                View
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

// ─── Prompt editor ───────────────────────────────────────────────────

export function PromptEditor({ initialContent, onSave, busy, liveVersion, draftDoc, hidePublish = false, readOnly = false }) {
  const [content, setContent] = useState(initialContent || "");

  useEffect(() => {
    setContent(initialContent || "");
  }, [initialContent]);

  const dirty = content !== initialContent;

  return (
    <div className="prompt-editor">
      <div className="prompt-editor-header">
        <div className="prompt-editor-stats">
          <span>{content.length.toLocaleString()} chars</span>
          <span>{content.split("\n").length.toLocaleString()} lines</span>
          {dirty && !readOnly && <span className="badge badge-draft">Unsaved</span>}
          {draftDoc && !dirty && <StatusBadge doc={draftDoc} />}
        </div>
        {!readOnly && (
          <div className="btn-row">
            <button
              className="btn primary"
              onClick={() => onSave(content, "draft")}
              disabled={busy || !dirty}
            >
              Save draft
            </button>
            {!hidePublish && (
              <button
                className="btn"
                onClick={() => {
                  if (window.confirm("Publish this content to Live? It will immediately apply to real callers.")) {
                    onSave(content, "live");
                  }
                }}
                disabled={busy || !dirty}
              >
                Save and Publish
              </button>
            )}
          </div>
        )}
      </div>
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        spellCheck="false"
        disabled={busy || readOnly}
        readOnly={readOnly}
      />
    </div>
  );
}

// ─── Read-only prompt viewer ─────────────────────────────────────────

export function PromptBody({ content }) {
  return <pre className="prompt-body">{content}</pre>;
}

// ─── Empty / loading / error states ──────────────────────────────────

export function EmptyState({ title, children }) {
  return (
    <div className="empty-state">
      <h3>{title}</h3>
      {children && <p>{children}</p>}
    </div>
  );
}

export function Loading({ label = "Loading..." }) {
  return <div className="loading">{label}</div>;
}

export function ErrorBanner({ error }) {
  if (!error) return null;
  return (
    <div className="error-banner">
      <strong>{error.name || "Error"}:</strong> {error.message}
      {error.status && <span className="mono" style={{ marginLeft: 8 }}>[{error.status}]</span>}
    </div>
  );
}

// ─── Stat cards ──────────────────────────────────────────────────────

export function StatCard({ label, value, sub }) {
  return (
    <div className="stat-card">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

// ─── Redacted field ──────────────────────────────────────────────────

export function RedactedField({ value, label }) {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center", padding: "6px 0" }}>
      <div className="muted small" style={{ minWidth: 180 }}>{label}</div>
      <div className="mono">{value || <span className="subtle">(none)</span>}</div>
    </div>
  );
}

// ─── Mode toggle ─────────────────────────────────────────────────────

export function ModeToggle({ mode, onChange }) {
  return (
    <div className="btn-row">
      <button
        className={`btn sm ${mode === "live" ? "primary" : ""}`}
        onClick={() => onChange("live")}
      >
        Live
      </button>
      <button
        className={`btn sm ${mode === "draft" ? "primary" : ""}`}
        onClick={() => onChange("draft")}
      >
        Draft
      </button>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────

export function formatDate(d) {
  if (!d) return "—";
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatCost(usd) {
  if (typeof usd !== "number" || isNaN(usd)) return "$0.00";
  return "$" + usd.toFixed(4);
}

export function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── DiffView ────────────────────────────────────────────────────────
//
// Side-by-side line diff, GitHub-style. Unchanged lines line up on
// both sides with a neutral background. Removed lines are red on the
// left and blank on the right; added lines are green on the right and
// blank on the left. Line numbers on each side.
//
// Props:
//   oldContent: string           — left side (previous / live)
//   newContent: string           — right side (current / draft)
//   oldLabel:   string           — header label for the left column
//   newLabel:   string           — header label for the right column
//   maxHeight:  CSS length       — max height for the scroll area
//
export function DiffView({
  oldContent = "",
  newContent = "",
  oldLabel = "Old",
  newLabel = "New",
  maxHeight = "55vh",
}) {
  const rows = useMemo(
    () => lineDiff(oldContent || "", newContent || ""),
    [oldContent, newContent]
  );
  const counts = useMemo(() => diffCounts(rows), [rows]);

  if (!oldContent && !newContent) {
    return <div className="muted">Nothing to compare.</div>;
  }

  return (
    <div className="diff-view">
      <div className="diff-view-header">
        <div className="diff-view-side">
          <span className="diff-view-label">{oldLabel}</span>
        </div>
        <div className="diff-view-stats">
          <span className="diff-stat-added">+{counts.added}</span>
          <span className="diff-stat-removed">−{counts.removed}</span>
        </div>
        <div className="diff-view-side">
          <span className="diff-view-label">{newLabel}</span>
        </div>
      </div>
      <div className="diff-view-body" style={{ maxHeight }}>
        {rows.length === 0 ? (
          <div className="diff-empty">Both sides are empty.</div>
        ) : (
          rows.map((r, idx) => <DiffRow key={idx} row={r} />)
        )}
      </div>
    </div>
  );
}

function DiffRow({ row }) {
  if (row.type === "equal") {
    return (
      <div className="diff-row diff-row-equal">
        <div className="diff-gutter">{row.oldNo}</div>
        <div className="diff-cell diff-cell-old">
          <span className="diff-marker"> </span>
          <span className="diff-text">{row.old || "\u00A0"}</span>
        </div>
        <div className="diff-gutter">{row.newNo}</div>
        <div className="diff-cell diff-cell-new">
          <span className="diff-marker"> </span>
          <span className="diff-text">{row.new || "\u00A0"}</span>
        </div>
      </div>
    );
  }
  if (row.type === "removed") {
    return (
      <div className="diff-row diff-row-removed">
        <div className="diff-gutter">{row.oldNo}</div>
        <div className="diff-cell diff-cell-old diff-removed">
          <span className="diff-marker">−</span>
          <span className="diff-text">{row.old || "\u00A0"}</span>
        </div>
        <div className="diff-gutter diff-gutter-empty"> </div>
        <div className="diff-cell diff-cell-empty"> </div>
      </div>
    );
  }
  // added
  return (
    <div className="diff-row diff-row-added">
      <div className="diff-gutter diff-gutter-empty"> </div>
      <div className="diff-cell diff-cell-empty"> </div>
      <div className="diff-gutter">{row.newNo}</div>
      <div className="diff-cell diff-cell-new diff-added">
        <span className="diff-marker">+</span>
        <span className="diff-text">{row.new || "\u00A0"}</span>
      </div>
    </div>
  );
}
